import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  InputAdornment,
  Box,
  MenuItem,
  TextField,
  Typography,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Autocomplete,
  Stack,
  Fade,
  FormControlLabel,
  Checkbox,
  IconButton,
  RadioGroup,
  Radio,
} from "@mui/material";
import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
  StarOutline as StarOutlineIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import DraggablePaper from "./DraggablePaper";
import { getGenericFieldTypeFromId } from "../utils/genericFieldTypes";
import { isGenericFormField } from "../../../../../../utils/genericFieldSupport";
import { getGenericPrefillError } from "../../../../../../utils/genericFieldPrefill";
import { isPreviewGroupLabelRecord } from "./preview/previewFieldLabel";
import InstanceFieldSettings from "./preview/InstanceFieldSettings";
import { applyFieldSettings } from "../../../../../../utils/fieldSettings";
import InsertedTextSettings from "./InsertedTextSettings";

const alignmentOptions = ["left", "center", "right"];
const geometryFieldConfigs = [
  {
    key: "x",
    label: "Left",
    aliases: ["x", "X", "left"],
    decimals: 5,
    tabIndex: 1,
  },
  {
    key: "width",
    label: "Width",
    aliases: ["width", "W"],
    decimals: 5,
    tabIndex: 2,
  },
  {
    key: "y",
    label: "Top",
    aliases: ["y", "Y", "top"],
    decimals: 5,
    tabIndex: 4,
  },
  {
    key: "height",
    label: "Height",
    aliases: ["height", "H"],
    decimals: 5,
    tabIndex: 3,
  },
];

const getValue = (field, ...keys) => {
  for (const key of keys) {
    if (field?.[key] !== undefined && field?.[key] !== null) {
      return field[key];
    }
  }
  return "";
};

const formatNumber = (value, decimals = 5, integerOnly = false) => {
  if (
    value === "" ||
    value === null ||
    value === undefined ||
    Number.isNaN(Number(value))
  ) {
    return "";
  }

  const numericValue = Number(value);

  if (integerOnly) {
    return String(Math.round(numericValue));
  }

  return numericValue
    .toFixed(decimals)
    .replace(/\.0+$/, "")
    .replace(/(\.\d*?)0+$/, "$1");
};

const sectionSx = {
  boxShadow: "none",
  border: "1px solid #e8eaed",
  borderRadius: "10px !important",
  "&:before": { display: "none" },
};

const OPERATORS = [
  { value: "+", label: "+ Add" },
  { value: "-", label: "− Subtract" },
  { value: "*", label: "× Multiply" },
  { value: "/", label: "÷ Divide" },
  { value: "%", label: "% Percentage of" },
];

const OP_SYMBOL = { "+": "+", "-": "−", "*": "×", "/": "÷", "%": "%" };

const FORMULA_FIELD_TYPES = ["number", "dollar", "money", "total"];
const FORMULA_FORMAT_OPTIONS = [
  { value: "default", label: "Existing/default format" },
  { value: "general", label: "General (1234.5)" },
  { value: "number", label: "Number (1,234.50)" },
  { value: "dollar", label: "Dollar ($1,234.50)" },
  { value: "money", label: "Money without symbol (1,234.50)" },
  { value: "percentage", label: "Percentage (12.50%)" },
];

const getInitialFormulaFormat = ({
  formulaFormat,
  formulaFields,
  fieldType,
  isGenericTableField,
}) => ({
  type: formulaFields?.length
    ? "default"
    : isGenericTableField || fieldType === "number"
      ? "general"
      : fieldType === "money"
        ? "money"
        : "dollar",
  decimalPlaces: 2,
  useGrouping: true,
  ...formulaFormat,
});

const getUniqueFieldOptions = (options) => {
  if (!Array.isArray(options)) return options;

  const fieldIds = new Set();

  return options.filter((option) => {
    if (!option?.field_id) return true;
    if (fieldIds.has(option.field_id)) return false;

    fieldIds.add(option.field_id);
    return true;
  });
};

const getParentKey = (field) => {
  if (field?.field_instance_id) {
    return `instance:${field.field_instance_id}`;
  }

  return field?.name ? `name:${field.name}` : null;
};

const ParentFieldDialog = ({
  open,
  allFields,
  selectedKeys,
  onClose,
  onConfirm,
}) => {
  const [pending, setPending] = React.useState([]);

  React.useEffect(() => {
    if (open) {
      setPending(
        selectedKeys.map((field) => ({
          ...field,
          value: getUniqueFieldOptions(field.value),
          options: getUniqueFieldOptions(field.options),
        })),
      );
    }
  }, [open, selectedKeys]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperComponent={DraggablePaper}
      aria-labelledby="draggable-dialog-title"
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.2)",
          },
        },
      }}
    >
      <DialogTitle
        id="draggable-dialog-title"
        style={{ cursor: "move" }}
        sx={{ pb: 1, fontSize: 14, fontWeight: 600 }}
      >
        Setup Computed Value
      </DialogTitle>

      <DialogContent sx={{ pt: "8px !important" }}>
        <Autocomplete
          multiple
          value={pending}
          onChange={(event, newValue) => {
            setPending(newValue);
          }}
          getOptionLabel={(option) => option.field_label}
          isOptionEqualToValue={(option, value) => {
            const key = getParentKey(option);
            return key !== null && key === getParentKey(value);
          }}
          filterSelectedOptions={true}
          options={allFields
            ?.filter((f) => !["PDFRectangle", "PDFLine"].includes(f.type))
            ?.map((f) => ({
              field_instance_id: f.field_instance_id,
              name: f.name,
              value: [],
              field_label: f.field_label,
              options: getUniqueFieldOptions(f.options),
            }))}
          fullWidth
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Parent Fields"
              name="field_type"
            />
          )}
        />

        <Stack gap={1} mt={1}>
          {pending?.map((field) => (
            <Box
              display={"flex"}
              alignItems={"center"}
              justifyContent={"space-between"}
              key={getParentKey(field)}
              gap={1}
            >
              <Typography sx={{ minWidth: "30%" }}>
                {field?.field_label}
              </Typography>
              <Autocomplete
                multiple
                size="small"
                value={field?.value || []}
                sx={{ minWidth: "68%" }}
                filterSelectedOptions
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                onChange={(event, newValue) => {
                  const targetKey = getParentKey(field);
                  if (!targetKey) return;

                  setPending((previous) =>
                    previous.map((parent) =>
                      getParentKey(parent) === targetKey
                        ? { ...parent, value: newValue }
                        : parent,
                    ),
                  );
                }}
                limitTags={1}
                renderTags={(values, getTagProps) =>
                  values.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });

                    return (
                      <Chip
                        key={key}
                        variant="outlined"
                        label={option.label}
                        size="small"
                        {...tagProps}
                      />
                    );
                  })
                }
                options={field?.options}
                fullWidth
                renderInput={(params) => (
                  <TextField {...params} size="small" label="Select Values" />
                )}
              />
            </Box>
          ))}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Button size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => onConfirm(pending)}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EMPTY_CALC_FIELDS = [{ field: "" }, { field: "", operator: "" }];
const EMPTY_SAME_FIELDS = [{ field: "" }];
const EQ_OPERATOR = { value: "=", label: "= Same as" };

const FormulaDialog = ({
  open,
  onClose,
  fieldOptions,
  formulaFields,
  formulaFormat,
  fieldType,
  isGenericTableField,
  onFieldChange,
}) => {
  const [mode, setMode] = useState("calc");
  const [draftFields, setDraftFields] = useState(formulaFields ?? "");
  const [draftFormat, setDraftFormat] = useState(() =>
    getInitialFormulaFormat({
      formulaFormat,
      formulaFields,
      fieldType,
      isGenericTableField,
    }),
  );
  const [bulkSearch, setBulkSearch] = useState("");
  const [bulkFieldNames, setBulkFieldNames] = useState([]);
  const [bulkOperator, setBulkOperator] = useState("+");
  const [bulkAddingCount, setBulkAddingCount] = useState(0);
  const pendingBulkAddRef = useRef(null);
  const isAddingBulkFields = bulkAddingCount > 0;

  useEffect(() => {
    pendingBulkAddRef.current = null;
    setBulkAddingCount(0);
    if (open) setDraftFields(formulaFields ?? "");
  }, [open, formulaFields]);

  useEffect(() => {
    if (open) {
      setDraftFormat(
        getInitialFormulaFormat({
          formulaFormat,
          formulaFields,
          fieldType,
          isGenericTableField,
        }),
      );
    }
  }, [open, formulaFormat, formulaFields, fieldType, isGenericTableField]);

  useEffect(() => {
    const request = pendingBulkAddRef.current;
    if (!open || !bulkAddingCount || !request) return;

    let updateFrame;
    // Give the loading indicator a paint opportunity before mounting the rows.
    const paintFrame = requestAnimationFrame(() => {
      updateFrame = requestAnimationFrame(() => {
        if (pendingBulkAddRef.current !== request) return;

        request.nextFields = [
          ...request.baseFields,
          ...request.selectedFields.map((field, index) => ({
            field: {
              name: field.name,
              field_label: field.field_label || field.name,
            },
            ...(request.baseFields.length + index > 0
              ? { operator: request.operator }
              : {}),
          })),
        ];
        setDraftFields(request.nextFields);
        setBulkFieldNames([]);
      });
    });

    return () => {
      cancelAnimationFrame(paintFrame);
      if (updateFrame !== undefined) cancelAnimationFrame(updateFrame);
    };
  }, [open, bulkAddingCount]);

  useEffect(() => {
    // Keep loading active until React has committed the new formula rows.
    if (pendingBulkAddRef.current?.nextFields === draftFields) {
      pendingBulkAddRef.current = null;
      setBulkAddingCount(0);
    }
  }, [draftFields]);

  const handleCloseFormula = () => {
    pendingBulkAddRef.current = null;
    setBulkAddingCount(0);
    onClose();
  };

  useEffect(() => {
    setBulkSearch("");
    setBulkFieldNames([]);
    setBulkOperator("+");
  }, [open, mode]);

  const fields = draftFields?.length > 0 ? draftFields : null;

  const isSameAs = useMemo(() => {
    if (fields?.length > 0) {
      return fields.some((f) => f?.operator?.value === "=");
    }
    return mode === "same";
  }, [fields, mode]);

  useEffect(() => {
    if (fields?.length > 0) {
      setMode(isSameAs ? "same" : "calc");
    }
  }, [fields, isSameAs]);

  const hasArithmeticOp = useMemo(
    () =>
      fields?.some((f) => ["+", "-", "*", "/"].includes(f?.operator?.value)) ??
      false,
    [fields],
  );

  const selectedFieldNames = useMemo(
    () => new Set(fields?.map((f) => f?.field?.name).filter(Boolean)),
    [fields],
  );

  const filteredOperators = useMemo(
    () => OPERATORS.filter((opt) => !(hasArithmeticOp && opt?.value === "=")),
    [hasArithmeticOp],
  );

  const filteredFieldOptions = useMemo(
    () => fieldOptions?.filter((opt) => !selectedFieldNames.has(opt?.name)),
    [fieldOptions, selectedFieldNames],
  );

  const availableBulkFields = useMemo(
    () =>
      Array.from(
        new Map(
          (filteredFieldOptions || [])
            .filter((field) => field?.name)
            .map((field) => [field.name, field]),
        ).values(),
      ),
    [filteredFieldOptions],
  );
  const matchingBulkFields = useMemo(() => {
    const search = bulkSearch.trim().toLowerCase();
    return availableBulkFields.filter((field) =>
      `${field.field_label || ""} ${field.name}`.toLowerCase().includes(search),
    );
  }, [availableBulkFields, bulkSearch]);
  const bulkSelection = useMemo(
    () => new Set(bulkFieldNames),
    [bulkFieldNames],
  );
  const selectedBulkFields = availableBulkFields.filter((field) =>
    bulkSelection.has(field.name),
  );
  const hasFormulaOperand = (step) =>
    Boolean(step?.field?.name) ||
    (step?.value !== "" && step?.value !== undefined && step?.value !== null);
  const bulkBaseFields = [...(fields || [])];
  // Remove only unused trailing placeholders; keep partially configured rows.
  while (
    bulkBaseFields.length &&
    !hasFormulaOperand(bulkBaseFields[bulkBaseFields.length - 1]) &&
    !bulkBaseFields[bulkBaseFields.length - 1]?.operator?.value
  ) {
    bulkBaseFields.pop();
  }
  const hasIncompleteFormula = bulkBaseFields.some(
    (step, index) =>
      !hasFormulaOperand(step) ||
      (index > 0 &&
        !OPERATORS.some((op) => op.value === step?.operator?.value)),
  );
  const handleAddBulkFields = () => {
    const operator = OPERATORS.find((option) => option.value === bulkOperator);
    if (
      pendingBulkAddRef.current ||
      isSameAs ||
      hasIncompleteFormula ||
      !selectedBulkFields.length ||
      !operator
    ) {
      return;
    }

    pendingBulkAddRef.current = {
      baseFields: bulkBaseFields,
      selectedFields: selectedBulkFields,
      operator,
    };
    setBulkAddingCount(selectedBulkFields.length);
  };

  const handleChangeFormulaField = useCallback(
    (name, val, index) => {
      if (pendingBulkAddRef.current) return;
      const updated =
        fields != null
          ? fields.map((f, i) => {
              if (i !== index) return f;

              const updatedField = { ...f, [name]: val };

              if (f?.operator?.value !== "%") {
                if (name === "field") updatedField.value = "";
                if (name === "value") updatedField.field = "";
              }

              return updatedField;
            })
          : [
              {
                [name]: val,
                ...(isSameAs ? { operator: EQ_OPERATOR } : {}),
              },
              ...(isSameAs ? [] : [{ field: "", operator: "" }]),
            ];

      setDraftFields(updated);
    },
    [fields, isSameAs],
  );

  const handleModeChange = useCallback((e) => {
    if (pendingBulkAddRef.current) return;
    setMode(e.target.value);
    setDraftFields("");
  }, []);

  const handleAddRow = useCallback(() => {
    if (pendingBulkAddRef.current) return;
    setDraftFields([...(fields ?? []), { field: "", operator: "" }]);
  }, [fields]);

  const displayFields =
    fields ?? (isSameAs ? EMPTY_SAME_FIELDS : EMPTY_CALC_FIELDS);
  const hasConfigurablePrecision = !["default", "general"].includes(
    draftFormat.type,
  );

  return (
    <Dialog open={open} onClose={handleCloseFormula} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          pb: 1,
          fontSize: 14,
          fontWeight: 600,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Formula builder
        <IconButton size="small" onClick={handleCloseFormula}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {isAddingBulkFields && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          role="status"
          aria-live="polite"
          sx={{ px: 3, py: 1 }}
        >
          <CircularProgress size={18} aria-label="Adding fields" />
          <Typography variant="body2">
            Adding {bulkAddingCount} fields… Please wait.
          </Typography>
        </Stack>
      )}

      <DialogContent
        sx={{ pt: "8px !important" }}
        aria-busy={isAddingBulkFields}
      >
        <Box
          component="fieldset"
          disabled={isAddingBulkFields}
          sx={{
            border: 0,
            p: 0,
            m: 0,
            minWidth: 0,
            pointerEvents: isAddingBulkFields ? "none" : "auto",
            opacity: isAddingBulkFields ? 0.6 : 1,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
          >
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 1.5,
                fontSize: "14px !important",
                color: "#666",
              }}
            >
              Add field
            </Typography>

            <RadioGroup row value={mode} onChange={handleModeChange}>
              <FormControlLabel
                value="same"
                control={<Radio />}
                label="Use Same Value"
              />
              <FormControlLabel
                value="calc"
                control={<Radio />}
                label="Calculate Field"
              />
            </RadioGroup>
          </Box>

          <Stack spacing={1.5} sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Result format
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                select
                fullWidth
                size="small"
                label="Format"
                value={draftFormat.type}
                onChange={(event) =>
                  setDraftFormat((previous) => ({
                    ...previous,
                    type: event.target.value,
                  }))
                }
                helperText={
                  draftFormat.type === "default"
                    ? "Keeps the existing widget formatting behavior."
                    : draftFormat.type === "general"
                      ? "Displays the calculated number without currency or fixed decimal places."
                      : draftFormat.type === "percentage"
                        ? "Adds a percent sign without changing the calculated value (12.5 becomes 12.50%)."
                        : "Formats the result independently of the widget type."
                }
              >
                {FORMULA_FORMAT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Decimal places"
                disabled={!hasConfigurablePrecision}
                value={draftFormat.decimalPlaces}
                onChange={(event) =>
                  setDraftFormat((previous) => ({
                    ...previous,
                    decimalPlaces: Number(event.target.value),
                  }))
                }
                sx={{ minWidth: 140 }}
              >
                {Array.from({ length: 7 }, (_, decimalPlaces) => (
                  <MenuItem key={decimalPlaces} value={decimalPlaces}>
                    {decimalPlaces}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <FormControlLabel
              disabled={!hasConfigurablePrecision}
              control={
                <Checkbox
                  checked={draftFormat.useGrouping}
                  onChange={(event) =>
                    setDraftFormat((previous) => ({
                      ...previous,
                      useGrouping: event.target.checked,
                    }))
                  }
                />
              }
              label="Use thousands separator"
            />
          </Stack>

          {!isSameAs && (
            <Accordion disableGutters sx={{ ...sectionSx, mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>
                  Add multiple fields
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1.5}>
                  <TextField
                    size="small"
                    label="Search fields by label or name"
                    value={bulkSearch}
                    onChange={(event) => setBulkSearch(event.target.value)}
                    fullWidth
                  />
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    <Button
                      size="small"
                      disabled={!matchingBulkFields.length}
                      onClick={() =>
                        setBulkFieldNames((names) => [
                          ...new Set([
                            ...names,
                            ...matchingBulkFields.map((field) => field.name),
                          ]),
                        ])
                      }
                    >
                      Select all matching ({matchingBulkFields.length})
                    </Button>
                    <Button size="small" onClick={() => setBulkFieldNames([])}>
                      Clear selection
                    </Button>
                    <Typography variant="body2">
                      {selectedBulkFields.length} selected
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      maxHeight: 260,
                      overflowY: "auto",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    {matchingBulkFields.length ? (
                      matchingBulkFields.map((field) => (
                        <FormControlLabel
                          key={field.name}
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            m: 0,
                            "& .MuiFormControlLabel-label": {
                              minWidth: 0,
                              py: 1,
                            },
                          }}
                          control={
                            <Checkbox
                              size="small"
                              checked={bulkSelection.has(field.name)}
                              onChange={(event) => {
                                const checked = event.target.checked;
                                setBulkFieldNames((names) =>
                                  checked
                                    ? [...new Set([...names, field.name])]
                                    : names.filter(
                                        (name) => name !== field.name,
                                      ),
                                );
                              }}
                            />
                          }
                          label={
                            <Box sx={{ overflowWrap: "anywhere" }}>
                              <Typography variant="body2">
                                {field.field_label || field.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {field.name}
                              </Typography>
                            </Box>
                          }
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No available fields match your search.
                      </Typography>
                    )}
                  </Box>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <TextField
                      select
                      size="small"
                      label="Operator between fields"
                      value={bulkOperator}
                      onChange={(event) => setBulkOperator(event.target.value)}
                      sx={{ minWidth: 210 }}
                    >
                      {OPERATORS.map((operator) => (
                        <MenuItem key={operator.value} value={operator.value}>
                          {operator.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      disabled={
                        isAddingBulkFields ||
                        !selectedBulkFields.length ||
                        hasIncompleteFormula
                      }
                      onClick={handleAddBulkFields}
                    >
                      {isAddingBulkFields
                        ? `Adding ${bulkAddingCount} fields…`
                        : `Add selected fields (${selectedBulkFields.length})`}
                    </Button>
                  </Stack>
                  <Typography
                    variant="caption"
                    color={hasIncompleteFormula ? "error" : "text.secondary"}
                  >
                    {hasIncompleteFormula
                      ? "Complete the partially configured formula rows before adding multiple fields."
                      : "Fields are appended in list order using the chosen operator. Existing entries stay unchanged. Click Done to save."}
                  </Typography>
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}

          <Stack spacing={0.75}>
            {displayFields.map((f, i) => (
              <Stack
                key={i}
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
                mb={2}
              >
                {i !== 0 && (
                  <Autocomplete
                    size="small"
                    value={f?.operator ?? ""}
                    disabled={isAddingBulkFields}
                    onChange={(_, newValue) =>
                      handleChangeFormulaField("operator", newValue, i)
                    }
                    getOptionLabel={(opt) => opt?.label ?? ""}
                    options={filteredOperators}
                    sx={{ width: { xs: "100%", sm: 160 }, flexShrink: 0 }}
                    renderInput={(params) => (
                      <TextField {...params} label="Operator" name="operator" />
                    )}
                  />
                )}

                <Autocomplete
                  size="small"
                  value={f?.field ?? ""}
                  disabled={isAddingBulkFields}
                  onChange={(_, newValue) =>
                    handleChangeFormulaField("field", newValue, i)
                  }
                  getOptionLabel={(opt) => opt?.field_label || opt?.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.name === value?.name
                  }
                  options={(fieldOptions || []).filter(
                    (option) =>
                      option.name === f?.field?.name ||
                      !selectedFieldNames.has(option.name),
                  )}
                  filterOptions={(options, { inputValue }) => {
                    const search = inputValue.trim().toLowerCase();
                    return options.filter((option) =>
                      `${option.field_label || ""} ${option.name || ""}`
                        .toLowerCase()
                        .includes(search),
                    );
                  }}
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props;
                    return (
                      <Box
                        component="li"
                        key={key}
                        {...optionProps}
                        sx={{ whiteSpace: "normal", overflowWrap: "anywhere" }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2">
                            {option.field_label || option.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.name}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  }}
                  sx={{ flex: 1, minWidth: 0 }}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select field"
                      name="field"
                      inputProps={{
                        ...params.inputProps,
                        title: f?.field?.name
                          ? `${f.field.field_label || f.field.name} (${f.field.name})`
                          : "",
                      }}
                    />
                  )}
                />

                {f?.operator?.value === "%" ? (
                  <TextField
                    label="Value"
                    size="small"
                    type="number"
                    sx={{ width: { xs: "100%", sm: 120 }, flexShrink: 0 }}
                    value={f?.value ?? ""}
                    onChange={(event) =>
                      handleChangeFormulaField("value", event.target.value, i)
                    }
                  />
                ) : (
                  <TextField
                    label="Number"
                    size="small"
                    type="number"
                    sx={{ width: { xs: "100%", sm: 120 }, flexShrink: 0 }}
                    value={f?.value ?? ""}
                    onChange={(event) =>
                      handleChangeFormulaField("value", event.target.value, i)
                    }
                  />
                )}

                {!isSameAs && (
                  <IconButton
                    onClick={handleAddRow}
                    disabled={
                      !f?.field &&
                      (f?.operator?.value === "%" ||
                        f?.value === "" ||
                        f?.value === undefined ||
                        f?.value === null)
                    }
                    sx={{
                      border: "1px solid #d0d7de",
                      borderRadius: 1.5,
                      p: "6px !important",
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                )}
              </Stack>
            ))}
          </Stack>

          {!isSameAs && (
            <>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1.5,
                  fontSize: "14px !important",
                  color: "#666",
                  mt: 2.5,
                }}
              >
                Formula preview
              </Typography>
              <Box
                sx={{
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  p: 1.5,
                  minHeight: 48,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.75,
                  alignItems: "center",
                }}
              >
                {!fields ? (
                  <Typography
                    variant="body2"
                    color="text.disabled"
                    fontStyle="italic"
                  >
                    No fields added yet
                  </Typography>
                ) : (
                  fields.map((e, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        minWidth: 0,
                        maxWidth: "100%",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {i > 0 && e?.operator?.value && (
                        <Typography>{OP_SYMBOL[e.operator.value]}</Typography>
                      )}
                      <Typography>
                        {e?.field?.field_label || e?.field?.name || e?.value}
                      </Typography>
                    </Box>
                  ))
                )}
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button
          disabled={isAddingBulkFields}
          onClick={() => {
            if (pendingBulkAddRef.current) return;
            onFieldChange?.("formulaFields", draftFields);
            // Do not opt existing formulas into new formatting on a no-op save.
            if (
              formulaFormat ||
              draftFormat.type !== "default" ||
              (!formulaFields?.length && draftFields?.length)
            ) {
              onFieldChange?.("formulaFormat", draftFormat);
            }
            onClose();
          }}
          variant="contained"
          size="small"
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const MIN_WIDTH = 280;
const MAX_WIDTH = 600;
const DEFAULT_WIDTH = 330;

const getInspectorSelectionKey = (field) =>
  field?.field_instance_id ?? field?.id ?? field?.name ?? field ?? null;

const hasSameInspectorSelection = (current, displayed) => {
  if (
    current.prefillFormId !== displayed.prefillFormId ||
    getInspectorSelectionKey(current.selectedField) !==
      getInspectorSelectionKey(displayed.selectedField) ||
    getInspectorSelectionKey(current.selectedTextItem) !==
      getInspectorSelectionKey(displayed.selectedTextItem) ||
    current.selectedTextItem?.page !== displayed.selectedTextItem?.page
  ) {
    return false;
  }

  const currentFields = current.selectedFields || [];
  const displayedFields = displayed.selectedFields || [];
  return (
    currentFields.length === displayedFields.length &&
    currentFields.every(
      (field, index) =>
        getInspectorSelectionKey(field) ===
        getInspectorSelectionKey(displayedFields[index]),
    )
  );
};

const ObjectInspectorDrawer = ({ isOpen, loading, ...props }) => {
  const {
    selectedField,
    selectedFields,
    selectedTextItem,
    pdfTextEdits,
    currentDocument,
    onFieldChange,
    onSelectedFieldsGeometryChange,
    onSelectedFieldsAlignmentChange,
    allFields,
    documentFields,
    prefillSourceFields,
    prefillFormId,
    onPrefillChange,
    textOverlayDisplayMode,
    handleRemoveFieldFromRow,
    onSelectedFieldsParentChange,
    onSelectedFieldsLabelChange,
    prefillPickTarget,
    prefillPick,
    onPrefillPickRequest,
    onPrefillPickConsumed,
  } = props;

  const snapshot = useMemo(
    () => ({
      selectedField,
      selectedFields,
      selectedTextItem,
      pdfTextEdits,
      currentDocument,
      onFieldChange,
      onSelectedFieldsGeometryChange,
      onSelectedFieldsAlignmentChange,
      allFields,
      documentFields,
      prefillSourceFields,
      prefillFormId,
      onPrefillChange,
      textOverlayDisplayMode,
      handleRemoveFieldFromRow,
      onSelectedFieldsParentChange,
      onSelectedFieldsLabelChange,
      prefillPickTarget,
      prefillPick,
      onPrefillPickRequest,
      onPrefillPickConsumed,
    }),
    [
      selectedField,
      selectedFields,
      selectedTextItem,
      pdfTextEdits,
      currentDocument,
      onFieldChange,
      onSelectedFieldsGeometryChange,
      onSelectedFieldsAlignmentChange,
      allFields,
      documentFields,
      prefillSourceFields,
      prefillFormId,
      onPrefillChange,
      textOverlayDisplayMode,
      handleRemoveFieldFromRow,
      onSelectedFieldsParentChange,
      onSelectedFieldsLabelChange,
      prefillPickTarget,
      prefillPick,
      onPrefillPickRequest,
      onPrefillPickConsumed,
    ],
  );
  const deferredSnapshot = useDeferredValue(snapshot);
  const hasSelection = Boolean(
    selectedField || selectedTextItem || selectedFields?.length,
  );
  const displayedSnapshot =
    hasSelection && !hasSameInspectorSelection(snapshot, deferredSnapshot)
      ? deferredSnapshot
      : snapshot;
  const isStale = displayedSnapshot !== snapshot;
  const committedSnapshotRef = useRef(snapshot);

  useLayoutEffect(() => {
    committedSnapshotRef.current = snapshot;
  }, [snapshot]);

  const guardedProps = useMemo(() => {
    const nextProps = { ...displayedSnapshot };
    for (const name of [
      "onFieldChange",
      "onPrefillChange",
      "onSelectedFieldsGeometryChange",
      "onSelectedFieldsAlignmentChange",
      "handleRemoveFieldFromRow",
      "onSelectedFieldsParentChange",
      "onSelectedFieldsLabelChange",
      "onPrefillPickRequest",
      "onPrefillPickConsumed",
    ]) {
      nextProps[name] = (...args) => {
        if (committedSnapshotRef.current !== displayedSnapshot) return;
        return displayedSnapshot[name]?.(...args);
      };
    }
    return nextProps;
  }, [displayedSnapshot]);

  const guardStaleInteraction = (event) => {
    if (!isStale) return;
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div
      style={{ display: "contents" }}
      aria-busy={isStale}
      onPointerDownCapture={guardStaleInteraction}
      onMouseDownCapture={guardStaleInteraction}
      onClickCapture={guardStaleInteraction}
      onDoubleClickCapture={guardStaleInteraction}
      onKeyDownCapture={guardStaleInteraction}
      onChangeCapture={guardStaleInteraction}
      onBlurCapture={guardStaleInteraction}
      onSubmitCapture={guardStaleInteraction}
    >
      <MemoizedObjectInspectorContent
        {...guardedProps}
        isOpen={isOpen}
        loading={loading}
      />
    </div>
  );
};

const GroupLabelInput = ({ label, onCommit, disabled }) => {
  const [draft, setDraft] = useState(label);
  const draftRef = useRef(label);

  useEffect(() => {
    draftRef.current = label;
    setDraft(label);
  }, [label]);

  const commitLabel = () => {
    const nextLabel = draftRef.current.trim();
    draftRef.current = nextLabel || label;
    setDraft(draftRef.current);
    if (!disabled && nextLabel && nextLabel !== label) {
      onCommit(nextLabel);
    }
  };

  return (
    <TextField
      label="Group label"
      size="small"
      disabled={disabled}
      value={draft}
      onChange={(event) => {
        draftRef.current = event.target.value;
        setDraft(event.target.value);
      }}
      onBlur={commitLabel}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          event.target.blur();
        } else if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          draftRef.current = label;
          setDraft(label);
          event.target.blur();
        }
      }}
    />
  );
};

const getPrefillDisplayLabel = (field) =>
  String(
    field?.field_label ||
      field?.label ||
      field?.title ||
      field?.field_name ||
      field?.name ||
      field?.field_id ||
      "Form field",
  );

const MAX_VISIBLE_SOURCE_OPTIONS = 100;

const filterSourceOptions = (options, { inputValue }) => {
  const search = inputValue.trim().toLowerCase();
  const matches = search
    ? options.filter((option) => option.searchText.includes(search))
    : options;
  // Document fields come first, so they always survive the cap.
  return matches.slice(0, MAX_VISIBLE_SOURCE_OPTIONS);
};

const sourceGroupHeaderStyle = {
  display: "block",
  padding: "6px 12px",
  fontSize: 12,
  fontWeight: 600,
  color: "#4b5563",
  background: "#f3f4f6",
  borderBottom: "1px solid #e5e7eb",
  position: "sticky",
  top: 0,
  zIndex: 1,
};
const sourceOptionLabelStyle = { fontSize: 14, lineHeight: 1.43 };
const sourceOptionCaptionStyle = { fontSize: 12, color: "#6b7280" };

const EXCLUDED_SOURCE_FIELD_TYPES = ["option", "selectGroup", "checkboxGroup"];

const PrefillSection = ({
  selectedField,
  formId,
  sourceFields,
  documentFields,
  onSave,
  loading,
  pickTarget,
  pick,
  onPickRequest,
  onPickConsumed,
}) => {
  const savedPrefill = selectedField.prefill;
  const [mode, setMode] = useState(savedPrefill?.mode || "field");
  const [sourceId, setSourceId] = useState(savedPrefill?.source_field_id || "");
  const [sourceInstanceId, setSourceInstanceId] = useState(
    savedPrefill?.source_field_instance_id || "",
  );
  const [manualValue, setManualValue] = useState(savedPrefill?.value ?? "");
  const [status, setStatus] = useState("");

  useEffect(() => {
    setMode(savedPrefill?.mode || "field");
    setSourceId(savedPrefill?.source_field_id || "");
    setSourceInstanceId(savedPrefill?.source_field_instance_id || "");
    setManualValue(savedPrefill?.value ?? "");
    setStatus("");
  }, [savedPrefill]);

  const sourceOptions = useMemo(() => {
    const byId = new Map();
    // Groups and everything nested under them are not valid prefill sources.
    const collect = (items, insideGroup = false) => {
      if (!Array.isArray(items)) return;
      items.forEach((field) => {
        const excluded =
          insideGroup ||
          EXCLUDED_SOURCE_FIELD_TYPES.includes(field?.field_type);
        if (!excluded && field?.field_id && !byId.has(field.field_id)) {
          byId.set(field.field_id, field);
        }
        collect(field?.options, excluded);
      });
    };
    // Use the complete catalog, not the document's combined/tagged field list.
    collect(sourceFields);
    return [...byId.values()];
  }, [sourceFields]);

  const unifiedOptions = useMemo(() => {
    const catalog = new Map(sourceOptions.map((f) => [f.field_id, f]));
    const documentFieldIds = new Set();
    const onDocument = [];

    (documentFields || []).forEach((field) => {
      if (!field?.field_id || !field.field_instance_id) return;
      if (field.form_id != null && field.form_id !== formId) return;
      if (EXCLUDED_SOURCE_FIELD_TYPES.includes(field.field_type)) return;
      if (!catalog.has(field.field_id)) return; // skips generic_table / untagged
      documentFieldIds.add(field.field_id);
      const page = field.page_num ?? field.page;
      const catalogLabel = getPrefillDisplayLabel(catalog.get(field.field_id));
      const instanceLabel = getPrefillDisplayLabel(field);
      onDocument.push({
        key: `instance:${field.field_instance_id}`,
        fieldId: field.field_id,
        instanceId: field.field_instance_id,
        label:
          instanceLabel === catalogLabel
            ? catalogLabel
            : `${catalogLabel} (${instanceLabel})`,
        caption: [page ? `Page ${page}` : ""].filter(Boolean).join(" · "),
        group: "On this document",
        searchText:
          `${catalogLabel} ${instanceLabel} ${field.name || ""} ${field.field_id}`.toLowerCase(),
      });
    });

    const others = sourceOptions
      .filter(
        (f) =>
          !documentFieldIds.has(f.field_id) ||
          // keep a saved field-only source selectable
          (f.field_id === sourceId && !sourceInstanceId),
      )
      .map((f) => ({
        key: `field:${f.field_id}`,
        fieldId: f.field_id,
        instanceId: "",
        label: getPrefillDisplayLabel(f),
        caption: "Not on this document",
        group: "Other form fields",
        searchText: `${getPrefillDisplayLabel(f)} ${f.field_id}`.toLowerCase(),
      }));

    // groupBy needs each group contiguous
    return [...onDocument, ...others];
  }, [sourceOptions, documentFields, formId, sourceId, sourceInstanceId]);

  const selectedSource =
    unifiedOptions.find((option) =>
      sourceInstanceId
        ? option.instanceId === sourceInstanceId
        : !option.instanceId && option.fieldId === sourceId,
    ) || null;

  const pickActive =
    Boolean(pickTarget) && pickTarget === selectedField.field_instance_id;

  const applyFavouritePick = (pickedFieldId) => {
    const candidates = unifiedOptions.filter(
      (option) =>
        option.fieldId === String(pickedFieldId) &&
        option.instanceId !== selectedField.field_instance_id,
    );
    const chosen = candidates[0];
    if (!chosen) {
      setStatus("This favorite can't be used as a prefill source here.");
      return;
    }
    setMode("field");
    setSourceId(chosen.fieldId);
    setSourceInstanceId(chosen.instanceId);
    setStatus(
      candidates.length > 1
        ? `Using ${chosen.caption}. This field appears ${candidates.length} times; change it in the list if needed.`
        : "",
    );
  };

  const lastPickRef = useRef(null);
  useEffect(() => {
    if (
      !pick ||
      pick.targetInstanceId !== selectedField.field_instance_id ||
      lastPickRef.current === pick.nonce
    ) {
      return;
    }
    lastPickRef.current = pick.nonce;
    applyFavouritePick(pick.fieldId);
    onPickConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pick]);

  const draft =
    mode === "manual"
      ? { mode: "manual", value: manualValue }
      : {
          mode: "field",
          source_field_id: sourceId,
          ...(sourceInstanceId
            ? { source_field_instance_id: sourceInstanceId }
            : {}),
        };
  const targetContext = {
    formId,
    fields: documentFields || [],
    targetInstanceId: selectedField.field_instance_id,
  };
  const targetError = getGenericPrefillError({
    ...targetContext,
    prefill: null,
  });
  const error =
    targetError ||
    (mode === "field" && sourceId && !selectedSource
      ? "The saved source is not available in the field catalog. Select a source again."
      : getGenericPrefillError({ ...targetContext, prefill: draft }));
  const disabled = Boolean(loading || targetError);

  const commit = (configuration) => {
    if (loading || targetError || (configuration !== null && error)) return;
    const accepted = onSave?.(configuration, {
      formId,
      targetInstanceId: selectedField.field_instance_id,
    });
    if (accepted === true) {
      setStatus("Configuration updated. Save the document to persist it.");
    }
  };

  return (
    <Accordion defaultExpanded disableGutters sx={sectionSx}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ fontWeight: 600 }}>Prefill</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1.25}>
          <RadioGroup
            aria-label="Prefill mode"
            value={mode}
            onChange={(event) => {
              setMode(event.target.value);
              setStatus("");
            }}
          >
            <FormControlLabel
              value="field"
              control={<Radio size="small" />}
              label="Prefill from a field"
              disabled={disabled}
            />
            <FormControlLabel
              value="manual"
              control={<Radio size="small" />}
              label="Manual prefill"
              disabled={disabled}
            />
          </RadioGroup>
          {mode === "manual" ? (
            <TextField
              label="Prefill value"
              size="small"
              multiline
              minRows={2}
              disabled={disabled}
              value={String(manualValue ?? "")}
              onChange={(event) => {
                setManualValue(event.target.value);
                setStatus("");
              }}
            />
          ) : (
            <>
              {onPickRequest && (
                <Button
                  size="small"
                  variant={pickActive ? "contained" : "outlined"}
                  startIcon={<StarOutlineIcon />}
                  disabled={disabled}
                  onClick={() =>
                    onPickRequest(
                      pickActive ? null : selectedField.field_instance_id,
                    )
                  }
                >
                  {pickActive
                    ? "Cancel (click a favorite)"
                    : "Pick from favorites"}
                </Button>
              )}
              <Autocomplete
                size="small"
                disabled={disabled}
                options={unifiedOptions}
                value={selectedSource}
                groupBy={(option) => option.group}
                getOptionKey={(option) => option.key}
                getOptionLabel={(option) => option.label}
                getOptionDisabled={(option) =>
                  option.instanceId === selectedField.field_instance_id
                }
                isOptionEqualToValue={(option, value) =>
                  option.key === value.key
                }
                filterOptions={filterSourceOptions}
                onChange={(_, option) => {
                  setSourceId(option?.fieldId || "");
                  setSourceInstanceId(option?.instanceId || "");
                  setStatus("");
                }}
                slotProps={{
                  paper: {
                    sx: {
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                    },
                  },
                  listbox: { sx: { maxHeight: 280, py: 0 } },
                }}
                renderGroup={(params) => (
                  <li key={params.key}>
                    <span style={sourceGroupHeaderStyle}>{params.group}</span>
                    <ul style={{ padding: 0 }}>{params.children}</ul>
                  </li>
                )}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;
                  return (
                    <li key={key} {...optionProps}>
                      <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                        <div style={sourceOptionLabelStyle}>{option.label}</div>
                        <div style={sourceOptionCaptionStyle}>
                          {option.caption}
                        </div>
                      </div>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Source form field"
                    helperText="Select a document field or search a form field."
                  />
                )}
              />
            </>
          )}
          {error && (
            <Typography variant="caption" color="error" role="alert">
              {error}
            </Typography>
          )}
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              disabled={disabled || Boolean(error)}
              onClick={() => commit(draft)}
            >
              Save
            </Button>
            {savedPrefill != null && (
              <Button
                size="small"
                color="error"
                disabled={disabled}
                onClick={() => commit(null)}
              >
                Remove
              </Button>
            )}
          </Stack>
          {status && (
            <Typography variant="caption" color="text.secondary" role="status">
              {status}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            Applies only to this generic widget. Save the document to persist
            changes.
          </Typography>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const LabelInput = ({ value, disabled, onCommit }) => {
  const [draft, setDraft] = useState(value ?? "");
  const committedRef = useRef(value ?? "");
  const timerRef = useRef(null);
  const onCommitRef = useRef(onCommit);

  useLayoutEffect(() => {
    onCommitRef.current = onCommit;
  });

  useEffect(() => {
    if (value === committedRef.current) return;
    committedRef.current = value ?? "";
    setDraft(value ?? "");
  }, [value]);

  const commit = (next) => {
    if (next === committedRef.current) return;
    committedRef.current = next;
    onCommitRef.current?.(next);
  };

  const handleChange = (event) => {
    const next = event.target.value;
    setDraft(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      commit(next);
    }, 300);
  };

  const flush = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    commit(draft);
  };

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  return (
    <TextField
      label="Label"
      size="small"
      disabled={disabled}
      value={draft}
      onChange={handleChange}
      onBlur={flush}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          event.target.blur();
        }
      }}
    />
  );
};

const TextGeometryInput = ({ label, fieldKey, value, disabled, onCommit }) => {
  const [draft, setDraft] = useState(formatNumber(value, 5));

  useEffect(() => {
    setDraft(formatNumber(value, 5));
  }, [value]);

  const commit = () => {
    const parsed = Number(draft);
    const isSize = fieldKey === "width" || fieldKey === "height";

    if (
      draft.trim() === "" ||
      !Number.isFinite(parsed) ||
      (isSize && parsed <= 0)
    ) {
      setDraft(formatNumber(value, 5));
      return;
    }

    if (formatNumber(parsed, 5) === formatNumber(value, 5)) return;
    onCommit(fieldKey, parsed);
  };

  return (
    <TextField
      label={label}
      size="small"
      type="number"
      disabled={disabled}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          event.currentTarget.querySelector("input")?.blur();
        } else if (event.key === "Escape") {
          event.preventDefault();
          setDraft(formatNumber(value, 5));
        }
      }}
      inputProps={{ step: 0.0001 }}
      InputProps={{
        endAdornment: <InputAdornment position="end">pt</InputAdornment>,
      }}
    />
  );
};

const ObjectInspectorContent = ({
  isOpen,
  selectedField,
  selectedFields = [],
  selectedTextItem,
  pdfTextEdits,
  currentDocument,
  onFieldChange,
  onSelectedFieldsGeometryChange,
  onSelectedFieldsAlignmentChange,
  loading,
  allFields,
  documentFields,
  prefillSourceFields,
  prefillFormId,
  onPrefillChange,
  textOverlayDisplayMode,
  handleRemoveFieldFromRow,
  onSelectedFieldsParentChange,
  onSelectedFieldsLabelChange,
  prefillPickTarget,
  prefillPick,
  onPrefillPickRequest,
  onPrefillPickConsumed,
}) => {
  const [expandedSections, setExpandedSections] = useState({
    geometry: true,
    general: true,
    appearance: true,
    advance: false,
  });
  const [geometryDrafts, setGeometryDrafts] = useState({});
  const [parentsDialogOpen, setParentsDialogOpen] = useState(false);
  const [configureFormulaOpen, setConfigureFormulaOpen] = useState(false);

  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const selectedInstanceId = selectedField?.field_instance_id ?? null;

  useEffect(() => {
    if (prefillPickTarget && prefillPickTarget !== selectedInstanceId) {
      onPrefillPickRequest?.(null);
    }
  }, [prefillPickTarget, selectedInstanceId, onPrefillPickRequest]);

  const parents = React.useMemo(() => {
    const raw = selectedField?.parents;
    if (!raw) return [];
    return Array.isArray(raw) ? raw : [raw];
  }, [selectedField?.parents]);

  const selectFields = useMemo(
    () =>
      allFields?.filter((f) =>
        ["selectGroup", "checkboxGroup"].includes(f?.field_type),
      ),
    [allFields],
  );

  const selectedCombineField = allFields?.find(
    (f) => f?.name === selectedField?.name,
  );

  const selectedGroup = selectedField?.field_id
    ? selectFields?.find(
        (field) =>
          field.field_id === selectedField.field_id ||
          field.options?.some(
            (option) => option.field_id === selectedField.field_id,
          ),
      )
    : null;
  const groupInstances = selectedGroup
    ? (documentFields || []).filter(
        (field) => field?.field_id === selectedGroup.field_id,
      )
    : [];
  const groupInstance =
    groupInstances.find((field) =>
      isPreviewGroupLabelRecord(field, selectedGroup?.field_type),
    ) ||
    (selectedGroup?.field_instance_id
      ? groupInstances.find(
          (field) =>
            field.field_instance_id === selectedGroup.field_instance_id,
        )
      : groupInstances.length === 1
        ? groupInstances[0]
        : groupInstances.find(
            (field) => selectedGroup?.name && field.name === selectedGroup.name,
          ));
  const groupLabel =
    [
      groupInstance?.field_label,
      groupInstance?.label,
      selectedGroup?.field_label,
      selectedGroup?.label,
      selectedGroup?.title,
    ].find((value) => typeof value === "string" && value.trim()) ?? "";

  const isSelectedGenericTableField =
    String(selectedField?.field_id || "").toLowerCase() === "generic_table" &&
    Boolean(selectedField?.generic_table?.table_id);
  const selectedGenericFieldType =
    selectedField?.generic_table?.field_type ||
    selectedField?.field_type ||
    "text";
  const selectedFormulaFieldType = isSelectedGenericTableField
    ? selectedGenericFieldType
    : getGenericFieldTypeFromId(selectedField?.field_id) ||
      selectedField?.field_type ||
      selectedCombineField?.field_type;
  const isSelectedTotalField = selectedFormulaFieldType === "total";
  const canConfigureFormula = FORMULA_FIELD_TYPES.includes(
    selectedFormulaFieldType,
  );

  const formulaFieldOptions = useMemo(() => {
    if (!isSelectedGenericTableField) {
      return allFields
        ?.filter(
          (field) =>
            FORMULA_FIELD_TYPES.includes(
              getGenericFieldTypeFromId(field?.field_id) || field?.field_type,
            ) && field?.name !== selectedField?.name,
        )
        ?.map((field) => ({
          name: field.name,
          field_label: field.field_label,
        }));
    }

    // Table widgets are not necessarily present in the combined field catalog.
    const sourcesByName = new Map(
      (allFields || []).map((field) => [field.name, field]),
    );
    (documentFields || []).forEach((field) => {
      if (
        String(field?.field_id || "").toLowerCase() === "generic_table" &&
        field?.generic_table?.table_id
      ) {
        sourcesByName.set(field.name, field);
      }
    });

    return Array.from(sourcesByName.values())
      .filter(
        (field) =>
          field?.name &&
          field.name !== selectedField?.name &&
          FORMULA_FIELD_TYPES.includes(
            field?.generic_table?.field_type ||
              getGenericFieldTypeFromId(field?.field_id) ||
              field?.field_type,
          ),
      )
      .map((field) => ({
        name: field.name,
        field_label: field.field_label || field.label || field.name,
      }));
  }, [
    allFields,
    documentFields,
    isSelectedGenericTableField,
    selectedField?.name,
  ]);

  React.useEffect(() => {
    if (!selectedField) {
      setGeometryDrafts({});
      return;
    }

    const nextGeometryDrafts = geometryFieldConfigs.reduce(
      (acc, fieldConfig) => {
        const rawValue = getValue(selectedField, ...fieldConfig.aliases);
        const displayValue = rawValue === "" ? "" : Number(rawValue);

        acc[fieldConfig.key] = formatNumber(displayValue, fieldConfig.decimals);
        return acc;
      },
      {},
    );

    setGeometryDrafts(nextGeometryDrafts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedField]);

  const handleSectionToggle = (sectionKey) => (_, expanded) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: expanded,
    }));
  };

  const commitGeometryValue = (fieldKey, aliases, decimals = 5) => {
    const draftValue = geometryDrafts[fieldKey];
    const isBulk = selectedFields.length >= 2;

    const currentValue = getValue(selectedField, ...aliases);
    if (
      formatNumber(draftValue, decimals) ===
      formatNumber(currentValue, decimals)
    ) {
      return;
    }

    if (draftValue === "") {
      if (isBulk) {
        onSelectedFieldsGeometryChange?.(fieldKey, "");
      } else {
        onFieldChange?.(fieldKey, "");
      }
      return;
    }

    const parsedValue = Number(draftValue);
    if (!Number.isFinite(parsedValue)) {
      const originalValue = getValue(selectedField, ...aliases);
      const displayValue = originalValue === "" ? "" : Number(originalValue);
      setGeometryDrafts((prev) => ({
        ...prev,
        [fieldKey]: formatNumber(displayValue, decimals),
      }));
      return;
    }

    if (isBulk) {
      onSelectedFieldsGeometryChange?.(fieldKey, parsedValue);
    } else {
      onFieldChange?.(fieldKey, parsedValue);
    }

    setGeometryDrafts((prev) => ({
      ...prev,
      [fieldKey]: formatNumber(parsedValue, decimals),
    }));
  };
  const onMouseDown = useCallback(
    (e) => {
      isResizing.current = true;
      startX.current = e.clientX;
      startWidth.current = width;

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const onMouseMove = (e) => {
        if (!isResizing.current) return;
        const delta = startX.current - e.clientX;
        const newWidth = Math.min(
          MAX_WIDTH,
          Math.max(MIN_WIDTH, startWidth.current + delta),
        );
        setWidth(newWidth);
      };

      const onMouseUp = () => {
        isResizing.current = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [width],
  );

  const selectedTextEdit = React.useMemo(() => {
    if (!selectedTextItem) return null;
    return (pdfTextEdits || []).find(
      (edit) => edit.target_item_id === selectedTextItem.id,
    );
  }, [pdfTextEdits, selectedTextItem]);

  const isInsertedText = Boolean(
    selectedTextItem &&
      (selectedTextItem.kind === "inserted" ||
        selectedTextItem.isEditorAdded === true ||
        (selectedTextEdit?.kind === "inserted" &&
          selectedTextEdit.isEditorAdded === true)),
  );

  const groupedFields = useMemo(() => {
    const isGenericTableField =
      String(selectedField?.field_id || "").toLowerCase() === "generic_table" &&
      Boolean(selectedField?.generic_table?.table_id);

    if (selectedField?.row) {
      const rowPrefix = selectedField.row.split("_")[0];
      const rowGroupSource = isGenericTableField ? documentFields : allFields;

      return rowGroupSource
        ?.filter(
          (f) =>
            f?.row?.includes(rowPrefix) ||
            f?.options?.some((opt) => opt?.row?.includes(rowPrefix)),
        )
        ?.map((f) => ({
          label: f?.field_label || f?.label,
          name: f?.name,
          row: f?.row,
          options: f?.options?.map((opt) => opt?.name),
        }));
    }

    if (
      !isGenericTableField ||
      selectedField?.generic_table?.row_group_disabled
    ) {
      return [];
    }

    const tableId = selectedField.generic_table.table_id;
    const selectedRowIndex = Number(selectedField.generic_table.row_index);
    const selectedColumnIndex = Number(
      selectedField.generic_table.column_index,
    );
    const tableFields = (documentFields || []).filter(
      (field) =>
        String(field?.field_id || "").toLowerCase() === "generic_table" &&
        field?.generic_table?.table_id === tableId,
    );
    const templateField = tableFields
      .filter(
        (field) =>
          Number(field?.generic_table?.column_index) === selectedColumnIndex &&
          Boolean(field?.row),
      )
      .sort(
        (a, b) =>
          Number(a?.generic_table?.row_index) -
          Number(b?.generic_table?.row_index),
      )[0];

    if (!templateField?.row) return [];

    const templateRowIndex = Number(templateField.generic_table.row_index);
    const templateRowPrefix = templateField.row.split("_")[0];
    const groupedColumnIndexes = tableFields
      .filter(
        (field) =>
          Number(field?.generic_table?.row_index) === templateRowIndex &&
          field?.row?.split("_")[0] === templateRowPrefix,
      )
      .map((field) => Number(field?.generic_table?.column_index));
    const groupedColumnIndexSet = new Set(groupedColumnIndexes);
    const inheritedGroupFields = tableFields.filter(
      (field) =>
        Number(field?.generic_table?.row_index) === selectedRowIndex &&
        groupedColumnIndexSet.has(Number(field?.generic_table?.column_index)) &&
        !field?.generic_table?.row_group_disabled,
    );

    if (inheritedGroupFields.length < 2) return [];

    return inheritedGroupFields.map((field) => ({
      label: field?.field_label || field?.label,
      name: field?.name,
      row: field?.row,
      options: field?.options?.map((option) => option?.name),
      genericRowGroupContext: {
        tableId,
        rowIndex: selectedRowIndex,
        columnIndex: Number(field?.generic_table?.column_index),
        groupedColumnIndexes,
        inherited: true,
      },
    }));
  }, [allFields, documentFields, selectedField]);

  const isGenericField = Boolean(
    getGenericFieldTypeFromId(selectedField?.field_id),
  );
  const selectedGenFieldType = getGenericFieldTypeFromId(
    selectedField?.field_id,
  );

  return (
    <Fade in>
      <Box sx={{ display: "flex", position: "relative" }}>
        {isOpen && (
          <Box
            onMouseDown={onMouseDown}
            sx={{
              width: 5,
              cursor: "col-resize",
              bgcolor: "transparent",
              borderRight: "1px solid #E5E7EB",
              flexShrink: 0,
              transition: "background-color 0.15s",
              "&:hover": {
                bgcolor: "primary.main",
                opacity: 0.4,
              },
              "&:active": {
                bgcolor: "primary.main",
                opacity: 0.6,
              },
            }}
          />
        )}
        <Box
          sx={{
            width: isOpen ? width : 0,
            minWidth: isOpen ? MIN_WIDTH : 0,
            maxWidth: isOpen ? MAX_WIDTH : 0,
            height: "100%",
            transition: "all 260ms ease",
            overflow: "hidden",
            borderLeft: isOpen ? "1px solid #e0e0e0" : "none",
            backgroundColor: "#fff",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ p: 2, borderBottom: "1px solid #f0f0f0" }}>
            <Typography
              variant="subtitle1"
              align="center"
              sx={{ fontWeight: 700 }}
            >
              {selectedTextItem
                ? isInsertedText
                  ? "Added Text"
                  : "PDF Text"
                : selectedField && selectedField?.type === "PDFTextField"
                  ? "Text Field"
                  : selectedField && selectedField?.type === "PDFCheckBox"
                    ? "Check Box"
                    : "Object Inspector"}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              overflowY: "auto",
              height: "calc(100% - 158px)",
            }}
          >
            {selectedTextItem ? (
              <>
                <Accordion
                  expanded={expandedSections.geometry}
                  onChange={handleSectionToggle("geometry")}
                  disableGutters
                  sx={sectionSx}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {isInsertedText
                        ? "Added Text Geometry"
                        : "PDF Text Geometry"}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 1.25,
                      }}
                    >
                      {[
                        [
                          "x",
                          "Left",
                          selectedTextEdit?.rect?.X ??
                            selectedTextItem?.rect?.X,
                        ],
                        [
                          "width",
                          "Width",
                          selectedTextEdit?.rect?.W ??
                            selectedTextItem?.rect?.W,
                        ],
                        [
                          "y",
                          "Top",
                          selectedTextEdit?.rect?.Y ??
                            selectedTextItem?.rect?.Y,
                        ],
                        [
                          "height",
                          "Height",
                          selectedTextEdit?.rect?.H ??
                            selectedTextItem?.rect?.H,
                        ],
                      ].map(([key, label, value]) => (
                        <TextGeometryInput
                          key={`${selectedTextEdit?.edit_id || selectedTextItem?.id}:${key}`}
                          fieldKey={key}
                          label={label}
                          value={value}
                          disabled={
                            loading ||
                            !selectedTextEdit ||
                            selectedTextEdit.action === "delete"
                          }
                          onCommit={onFieldChange}
                        />
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  expanded={expandedSections.general}
                  onChange={handleSectionToggle("general")}
                  disableGutters
                  sx={sectionSx}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {isInsertedText ? "Text Settings" : "PDF Text"}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {isInsertedText ? (
                      <InsertedTextSettings
                        key={selectedTextEdit?.edit_id || selectedTextItem.id}
                        edit={selectedTextEdit}
                        documentKey={currentDocument?.base64File}
                        onFieldChange={onFieldChange}
                        active={isOpen}
                        disabled={
                          loading ||
                          !selectedTextEdit ||
                          selectedTextEdit.action === "delete"
                        }
                      />
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.25,
                        }}
                      >
                        <TextField
                          label="Original Text"
                          size="small"
                          value={selectedTextItem?.text || ""}
                          InputProps={{ readOnly: true }}
                        />
                        <TextField
                          label="Replacement Text"
                          size="small"
                          disabled={loading}
                          multiline
                          minRows={2}
                          value={
                            selectedTextEdit?.new_text ??
                            selectedTextItem?.text ??
                            ""
                          }
                          onChange={(event) =>
                            onFieldChange?.("text", event.target.value)
                          }
                        />
                        <TextField
                          label="Action"
                          size="small"
                          disabled={loading}
                          select
                          value={selectedTextEdit?.action || "replace"}
                          onChange={(event) =>
                            onFieldChange?.("action", event.target.value)
                          }
                        >
                          <MenuItem value="replace">replace</MenuItem>
                          <MenuItem value="move">move</MenuItem>
                          <MenuItem value="delete">delete</MenuItem>
                        </TextField>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              </>
            ) : selectedField ? (
              <>
                {!selectedField?.hidden && (
                  <Accordion
                    expanded={expandedSections.geometry}
                    onChange={handleSectionToggle("geometry")}
                    disableGutters
                    sx={sectionSx}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ fontWeight: 600 }}>Geometry</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 1.25,
                        }}
                      >
                        {geometryFieldConfigs.map((fieldConfig) => (
                          <TextField
                            key={fieldConfig.key}
                            label={fieldConfig.label}
                            size="small"
                            type="number"
                            disabled={loading}
                            value={geometryDrafts[fieldConfig.key] ?? ""}
                            onChange={(event) => {
                              setGeometryDrafts((prev) => ({
                                ...prev,
                                [fieldConfig.key]: event.target.value,
                              }));
                            }}
                            onBlur={() =>
                              commitGeometryValue(
                                fieldConfig.key,
                                fieldConfig.aliases,
                                fieldConfig.decimals,
                              )
                            }
                            inputProps={{
                              step: 0.0001,
                              tabIndex: fieldConfig.tabIndex,
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  pt
                                </InputAdornment>
                              ),
                            }}
                          />
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                )}

                <Accordion
                  expanded={expandedSections.general}
                  onChange={handleSectionToggle("general")}
                  disableGutters
                  sx={sectionSx}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 600 }}>General</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.25,
                      }}
                    >
                      {selectedGroup && (
                        <GroupLabelInput
                          key={JSON.stringify([
                            getInspectorSelectionKey(selectedField),
                            selectedGroup.field_id,
                          ])}
                          label={groupLabel}
                          disabled={loading || !onFieldChange}
                          onCommit={(nextLabel) =>
                            onFieldChange?.("__group_label__", nextLabel)
                          }
                        />
                      )}

                      <LabelInput
                        key={getInspectorSelectionKey(selectedField)}
                        value={getValue(
                          selectedField,
                          "field_label",
                          "label",
                          "placeholder",
                          "name",
                        )}
                        disabled={loading}
                        onCommit={(nextValue) => {
                          if (selectedFields.length >= 2) {
                            onSelectedFieldsLabelChange?.(nextValue);
                          } else {
                            onFieldChange?.("field_label", nextValue);
                            onFieldChange?.("label", nextValue);
                          }
                        }}
                      />

                      {selectedFields.length < 2 &&
                        selectedField?.field_id &&
                        selectedField?.location !== "sign" &&
                        !isSelectedGenericTableField &&
                        ![
                          "generic_full_title",
                          "generic_full_subTitle",
                          "generic_table",
                          "generic_checkbox_group",
                          "generic_select_group",
                          "generic_group_option",
                        ].includes(selectedField.field_id) &&
                        !(selectedGroup && isGenericFormField(selectedGroup)) &&
                        ![
                          "PDFRectangle",
                          "PDFLine",
                          "full_title",
                          "full_subTitle",
                        ].includes(
                          (
                            selectedGroup ||
                            selectedCombineField ||
                            selectedField
                          )?.field_type ??
                            (
                              selectedGroup ||
                              selectedCombineField ||
                              selectedField
                            )?.type,
                        ) && (
                          <Box
                            key={`instance-settings:${
                              selectedGroup?.field_id ||
                              getInspectorSelectionKey(selectedField)
                            }`}
                            sx={{
                              mt: 0.5,
                              pt: 1.5,
                              borderTop: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ mb: 0.5, fontWeight: 600 }}
                            >
                              {["checkboxGroup", "selectGroup"].includes(
                                (
                                  selectedGroup ||
                                  selectedCombineField ||
                                  selectedField
                                )?.field_type ??
                                  (
                                    selectedGroup ||
                                    selectedCombineField ||
                                    selectedField
                                  )?.type,
                              )
                                ? "Group Settings"
                                : "Field Settings"}
                            </Typography>

                            <InstanceFieldSettings
                              field={applyFieldSettings(
                                selectedGroup ||
                                  selectedCombineField ||
                                  selectedField,
                              )}
                              overrides={
                                (selectedGroup ? groupInstance : selectedField)
                                  ?.field_settings
                              }
                              disabled={loading || !onFieldChange}
                              onChange={(patch) =>
                                onFieldChange?.("__field_settings__", patch)
                              }
                            />
                          </Box>
                        )}

                      {isGenericField && (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ fontSize: "14px !important", color: "#666" }}
                          >
                            Generic Field :
                          </Typography>
                          <Chip
                            label={selectedGenFieldType}
                            size="small"
                            variant="filled"
                            sx={{
                              fontSize: "0.7rem",
                              height: 22,
                              borderRadius: 1,
                            }}
                          />
                        </Box>
                      )}
                      {isSelectedGenericTableField &&
                        selectedField?.type === "PDFTextField" && (
                          <TextField
                            select
                            label="Cell field type"
                            size="small"
                            disabled={loading}
                            value={selectedGenericFieldType}
                            onChange={(event) => {
                              const nextType = event.target.value;
                              onFieldChange?.("field_type", nextType);
                              if (nextType === "total") {
                                setExpandedSections((sections) => ({
                                  ...sections,
                                  advance: true,
                                }));
                              }
                            }}
                            helperText="Applies only to this cell, not the entire column."
                          >
                            <MenuItem value="text">Text</MenuItem>
                            <MenuItem value="number">Number</MenuItem>
                            <MenuItem value="total">Total</MenuItem>
                          </TextField>
                        )}
                    </Box>
                  </AccordionDetails>
                </Accordion>

                {selectedFields.length < 2 &&
                  isGenericFormField(selectedField) && (
                    <PrefillSection
                      key={JSON.stringify([
                        prefillFormId,
                        selectedField.field_instance_id,
                        selectedField.field_id,
                      ])}
                      selectedField={selectedField}
                      formId={prefillFormId}
                      sourceFields={prefillSourceFields}
                      documentFields={documentFields}
                      onSave={onPrefillChange}
                      loading={loading}
                      pickTarget={prefillPickTarget}
                      pick={prefillPick}
                      onPickRequest={onPrefillPickRequest}
                      onPickConsumed={onPrefillPickConsumed}
                    />
                  )}

                {!selectedField?.hidden && (
                  <Accordion
                    expanded={expandedSections.appearance}
                    onChange={handleSectionToggle("appearance")}
                    disableGutters
                    sx={sectionSx}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ fontWeight: 600 }}>
                        Appearance
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.25,
                        }}
                      >
                        <TextField
                          label="Line Width"
                          size="small"
                          type="number"
                          disabled={
                            (!selectedField?.outline &&
                              !["PDFRectangle", "PDFLine"].includes(
                                selectedField?.type,
                              )) ||
                            loading
                          }
                          value={
                            getValue(selectedField, "lineWidth", "alignment") ||
                            1
                          }
                          onChange={(event) =>
                            onFieldChange?.(
                              "lineWidth",
                              Number(event.target.value),
                            )
                          }
                        />
                        <TextField
                          label="Alignment"
                          size="small"
                          disabled={loading}
                          select
                          value={
                            getValue(selectedField, "textAlign", "alignment") ||
                            "left"
                          }
                          onChange={(event) => {
                            const nextAlignment = event.target.value;
                            if (selectedFields.length >= 2) {
                              onSelectedFieldsAlignmentChange?.(nextAlignment);
                            } else {
                              onFieldChange?.("textAlign", nextAlignment);
                            }
                          }}
                        >
                          {alignmentOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                )}

                <Accordion
                  expanded={expandedSections.advance}
                  onChange={handleSectionToggle("advance")}
                  disableGutters
                  sx={sectionSx}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 600 }}>Advance</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.25,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mb: 0.5,
                            fontSize: "14px !important",
                            color: "#666",
                          }}
                        >
                          Parents
                        </Typography>

                        {/* Chips */}
                        {parents.length > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5,
                              mb: 0.75,
                            }}
                          >
                            {parents.map((parent, parentIndex) => {
                              const field = selectFields.find(
                                (f) =>
                                  f.field_instance_id ===
                                  parent.field_instance_id,
                              );
                              return (
                                <Chip
                                  key={
                                    getParentKey(parent) ??
                                    `parent-${parentIndex}`
                                  }
                                  label={
                                    parent?.field_label ??
                                    field?.name ??
                                    parent.name
                                  }
                                  size="small"
                                  onDelete={() =>
                                    onFieldChange?.(
                                      "parents",
                                      parents.filter(
                                        (_, index) => index !== parentIndex,
                                      ),
                                    )
                                  }
                                />
                              );
                            })}
                          </Box>
                        )}

                        {/* Add parent button */}
                        <Button
                          size="medium"
                          variant="outlined"
                          startIcon={<AddIcon />}
                          disabled={loading}
                          onClick={() => setParentsDialogOpen(true)}
                          sx={{
                            fontSize: 11,
                          }}
                        >
                          Add parent
                        </Button>
                      </Box>

                      {canConfigureFormula && (
                        <>
                          {(isSelectedTotalField ||
                            selectedField?.formulaFields?.length > 0) && (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={
                                    selectedField?.isEditable === true ||
                                    selectedField?.isEditable === "true"
                                  }
                                  onChange={(e) =>
                                    onFieldChange?.(
                                      "isEditable",
                                      e.target.checked,
                                    )
                                  }
                                />
                              }
                              label="Is Editable?"
                            />
                          )}

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                mb: 0.5,
                                fontSize: "14px !important",
                                color: "#666",
                              }}
                            >
                              Setup Computed Value
                            </Typography>

                            {isSelectedGenericTableField && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1 }}
                              >
                                {isSelectedTotalField
                                  ? "To total this column, select the Number cells above this cell and use the + Add operator between them. This Total cell is excluded from the source list."
                                  : "Use numeric fields or cells to calculate this cell's value. This cell is excluded from the source list."}
                              </Typography>
                            )}

                            {getValue(selectedField, "formulaFields")?.length >
                              0 && (
                              <Box
                                sx={{
                                  bgcolor: "action.hover",
                                  borderRadius: 1,
                                  p: 1,
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.75,
                                  alignItems: "center",
                                  mb: 1,
                                }}
                              >
                                {getValue(selectedField, "formulaFields").map(
                                  (e, i) => (
                                    <Box
                                      key={i}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      {i > 0 && e?.operator?.value && (
                                        <Typography sx={{ fontSize: "14px" }}>
                                          {OP_SYMBOL[e.operator.value]}
                                        </Typography>
                                      )}
                                      <Typography sx={{ fontSize: "14px" }}>
                                        {e?.field?.field_label ||
                                          e?.field?.name ||
                                          e?.value}
                                      </Typography>
                                    </Box>
                                  ),
                                )}
                              </Box>
                            )}
                            {/* Add parent button */}
                            <Button
                              size="medium"
                              variant="outlined"
                              startIcon={<AddIcon />}
                              disabled={loading}
                              onClick={() => setConfigureFormulaOpen(true)}
                              sx={{
                                fontSize: 11,
                              }}
                            >
                              Setup Computed Value
                            </Button>
                          </Box>
                        </>
                      )}

                      {groupedFields && groupedFields?.length > 0 && (
                        <Box>
                          <Box
                            display={"flex"}
                            alignItems={"center"}
                            justifyContent={"space-between"}
                            gap={1}
                            mb={1}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                mb: 0.5,
                                fontSize: "14px !important",
                                color: "#666",
                              }}
                            >
                              Field Group Visibility
                            </Typography>
                            <IconButton
                              size="small"
                              disabled={loading}
                              onClick={() =>
                                handleRemoveFieldFromRow(
                                  selectedField?.row,
                                  true,
                                  undefined,
                                  undefined,
                                  groupedFields[0]?.genericRowGroupContext,
                                )
                              }
                              sx={{
                                border: "1px solid",
                                borderColor: "error.light",
                                borderRadius: 1.5,
                                backgroundColor: "transparent",
                                p: "4px !important",
                              }}
                            >
                              <DeleteIcon
                                sx={{ fontSize: "1rem" }}
                                color="error"
                              />
                            </IconButton>
                          </Box>
                          <Box
                            display={"flex"}
                            alignItems="center"
                            gap={1}
                            flexDirection={"column"}
                          >
                            {groupedFields.map((f, i) => {
                              const displayLabel =
                                textOverlayDisplayMode === "none"
                                  ? `${f?.label} (${f?.name})`
                                  : textOverlayDisplayMode === "label"
                                    ? f?.label
                                    : f?.name;

                              return (
                                <Box
                                  display={"flex"}
                                  alignItems="center"
                                  gap={1}
                                  key={f?.name}
                                  justifyContent={"space-between"}
                                  width={"100%"}
                                >
                                  <Typography sx={{ fontSize: "14px" }}>
                                    {displayLabel}
                                  </Typography>
                                  <DeleteIcon
                                    sx={{ fontSize: "1rem", cursor: "pointer" }}
                                    color="error"
                                    onClick={() =>
                                      handleRemoveFieldFromRow(
                                        f?.row,
                                        false,
                                        displayLabel,
                                        f?.options,
                                        f?.genericRowGroupContext,
                                      )
                                    }
                                  />
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </>
            ) : (
              <Box sx={{ pt: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                >
                  Select a field on the canvas to inspect its details.
                </Typography>
              </Box>
            )}
          </Box>
          <FormulaDialog
            open={configureFormulaOpen}
            onClose={() => setConfigureFormulaOpen(false)}
            fieldOptions={formulaFieldOptions}
            formulaFields={getValue(selectedField, "formulaFields")}
            formulaFormat={selectedField?.formulaFormat}
            fieldType={selectedFormulaFieldType}
            isGenericTableField={isSelectedGenericTableField}
            onFieldChange={onFieldChange}
          />
          <ParentFieldDialog
            open={parentsDialogOpen}
            allFields={selectFields?.filter((f) =>
              f.field_instance_id
                ? f.field_instance_id !== selectedField?.field_instance_id
                : !f?.options?.some(
                    (opt) =>
                      opt?.field_instance_id &&
                      opt.field_instance_id ===
                        selectedField?.field_instance_id,
                  ),
            )}
            selectedKeys={parents}
            onClose={() => setParentsDialogOpen(false)}
            onConfirm={(nextParents) => {
              if (selectedFields.length >= 2) {
                onSelectedFieldsParentChange?.(nextParents);
              } else {
                onFieldChange?.("parents", nextParents);
              }
              setParentsDialogOpen(false);
            }}
          />
        </Box>
      </Box>
    </Fade>
  );
};

const MemoizedObjectInspectorContent = React.memo(ObjectInspectorContent);

export default ObjectInspectorDrawer;
